import React, { useMemo } from 'react'

import { CONTACT_UPDATE_SUPPORTED_INTEGRATIONS, getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import ProspectingSuccessLogo from '../../../assets/images/prospecting-success-logo.png'

import { Button } from '../../../components/Button/Button'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  clearDocumentProspectingDetailsForm,
  selectDocumentProspectingAttachment,
  selectDocumentProspectingContact,
  selectDocumentProspectingCurrentUser,
  selectDocumentProspectingServiceId,
  selectDocumentProspectingSuccessIntegrations,
  setDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'

import { useShowSummaryForDocumentProspecting } from './hooks'

interface ProspectingSuccessProps {
  onRestart: () => void
}

export const ProspectingSuccess: React.FC<ProspectingSuccessProps> = ({ onRestart }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const successIntegrations = useAppSelector(selectDocumentProspectingSuccessIntegrations)
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)
  const currentUser = useAppSelector(selectDocumentProspectingCurrentUser)
  const contact = useAppSelector(selectDocumentProspectingContact)
  const attachment = useAppSelector(selectDocumentProspectingAttachment)

  const { showSummary } = useShowSummaryForDocumentProspecting()

  const integrations = useMemo(
    () =>
      currentUser?.integrations
        ?.filter(({ serviceId }) => CONTACT_UPDATE_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId))
        .filter(({ serviceId }) => !successIntegrations.includes(serviceId as IntegrationServiceId)) ?? [],
    [currentUser, successIntegrations],
  )

  const _handleIntegrationSelected = (selectedServiceId: IntegrationServiceId) => {
    dispatch(clearDocumentProspectingDetailsForm())
    dispatch(setDocumentProspectingServiceId(selectedServiceId))
    onRestart()
  }

  const handleIntegrationSelected = tracked(
    _handleIntegrationSelected,
    { eventName: TrackEvent.DOCUMENT_PROSPECTING_EXTERNAL_SYSTEM_SELECTED },
    { eventDetailsExtractor: (selectedServiceId: IntegrationServiceId) => ({ targetSystem: selectedServiceId }) },
  )

  if (!serviceId || !contact || !attachment) {
    return null
  }

  const integrationDisplayName = getIntegrationDisplayName(serviceId)

  return (
    <div className='d-flex flex-column align-items-center zocks-gap-2 document-prospecting-success-container'>
      <img src={ProspectingSuccessLogo} alt='Prospecting Success' />
      <div className='d-flex flex-column align-items-center gap-2'>
        <h5>Contact created successfully</h5>
        <p>
          <span className='paragraph-medium'>{getUserDisplayName(contact)} has been added to</span>{' '}
          <span className='paragraph-medium zocks-text-primary'>Zocks</span> <span className='paragraph-medium'>and</span>{' '}
          <span className='paragraph-medium zocks-text-primary'>{integrationDisplayName}</span>
        </p>
      </div>
      <div className='d-flex flex-column align-items-center py-3 gap-4 zocks-border-0.75 zocks-bg-color-gray-100 w-100'>
        <h5>What would you like to do next?</h5>
        <div className='d-flex align-items-center justify-content-center flex-wrap gap-2 px-3'>
          <Button variant='primary' className='button-medium px-3 py-2' onClick={showSummary}>
            Get document summary
          </Button>
          {integrations.map(({ serviceId }) => (
            <div
              key={serviceId}
              className='px-3 py-2 button-medium zocks-text-gray-900 zocks-bg-color-white rounded-1 border zocks-border-color-gray-300 cursor-pointer'
              onClick={() => handleIntegrationSelected(serviceId as IntegrationServiceId)}
            >
              Add contact to {getIntegrationDisplayName(serviceId as IntegrationServiceId)}
            </div>
          ))}
        </div>
        <a
          className='paragraph-medium zocks-text-primary bold cursor-pointer'
          href='/dashboard/library/manage-contacts'
          target='_blank'
          rel='noopener noreferrer'
        >
          Go back to Contacts
        </a>
      </div>
    </div>
  )
}
